import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { t } from "@lingui/macro";
import { Box, Container, Divider, Tab, Tabs } from "@mui/material";
import {
  getWorkoutTypeFamilyWfIcon,
  RedesignrChip,
  WfButton,
  WfIconContentEditRegular,
  WfIconNavigationCloseRegular,
} from "@WahooFitness/redesignr";
import { ErrorBoundary, GlobalHeader, useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useEffect, useMemo, useState } from "react";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";
import CyclingProfile from "./CyclingProfile/CyclingProfile";
import RunningProfile from "./RunningProfile/RunningProfile";
import useEducationSection from "./Shared/Education/useEducationSection";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import { getWorkoutTypeNames } from "../WorkoutProfiles/AddWorkoutProfile/WorkoutTypeNames";
import FullScreenDialog from "../FullScreenDialog";
import ProfileSelectDialog from "./ProfileSelectDialog";
import useFitnessProfileSettings from "@/hooks/useFitnessProfileSettings";

const FitnessProfile = ({
  profile,
  onProfileChange,
}: {
  profile: WahooWorkoutTypeFamily;
  onProfileChange: (newProfile: WahooWorkoutTypeFamily) => void;
}) => {
  const { userFitnessProfilesIsLoading, userFitnessProfilesError, mutateUserFitnessProfile } =
    useFitnessProfiles();
  const handleReload = useCallback(() => {
    mutateUserFitnessProfile();
  }, [mutateUserFitnessProfile]);

  const { logEvent } = useLogEvent();

  const { enabled: showBetaTag } = useSettingsVersionedFeatures(
    "SHOW_RUNNING_ATHLETE_PROFILE_BETA_TAG"
  );

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: WahooWorkoutTypeFamily) => {
      logEvent("athlete_profile", {
        event_type: EventType.click,
        click_target:
          newValue === WahooWorkoutTypeFamily.Cycling
            ? ClickTarget.cycling_tab
            : ClickTarget.running_tab,
        source_page_name: SourcePage.fitness_profile,
      });
      onProfileChange(newValue);
    },
    [onProfileChange, logEvent]
  );

  const [profileSelectDialogOpen, setProfileSelectDialogOpen] = useState(false);

  const handleEditProfileClick = useCallback(() => {
    logEvent("athlete_profile", {
      event_type: EventType.click,
      click_target: ClickTarget.edit_tabs,
      source_page_name: SourcePage.fitness_profile,
    });
    setProfileSelectDialogOpen(true);
  }, [logEvent]);

  const closeProfileSelectDialog = useCallback(() => {
    setProfileSelectDialogOpen(false);
  }, []);

  const {
    selectedFitnessProfiles,
    updateSelectedFitnessProfiles,
    error: fitnessProfileSettingsError,
    isLoading: fitnessProfileSettingsIsLoading,
  } = useFitnessProfileSettings();

  // for deep linking to a profile: checking if that exists, otherwise go to the other one.
  useEffect(() => {
    if (
      selectedFitnessProfiles &&
      selectedFitnessProfiles.length > 0 &&
      !selectedFitnessProfiles.includes(profile)
    ) {
      onProfileChange(selectedFitnessProfiles[0]);
    }
  }, [onProfileChange, profile, selectedFitnessProfiles]);

  const { enqueueSnackbar } = useSnackbarContext();

  const handleProfileSelectionChange = useCallback(
    async (profiles: WahooWorkoutTypeFamily[]) => {
      const oldProfiles = selectedFitnessProfiles ?? [
        WahooWorkoutTypeFamily.Cycling,
        WahooWorkoutTypeFamily.Running,
      ];
      if (!profiles.includes(profile)) {
        onProfileChange(profiles[0]);
      }
      closeProfileSelectDialog();

      try {
        await updateSelectedFitnessProfiles(profiles);
        enqueueSnackbar({
          message: t`Your Athlete Profile settings were successfully updated.`,
          severity: "success",
        });
      } catch {
        updateSelectedFitnessProfiles(oldProfiles);
        enqueueSnackbar({
          message: t`There was a problem saving your profile. Check your network and try again.`,
          severity: "error",
        });
      }
    },
    [
      closeProfileSelectDialog,
      onProfileChange,
      profile,
      updateSelectedFitnessProfiles,
      enqueueSnackbar,
      selectedFitnessProfiles,
    ]
  );

  const workoutTypeNames = useMemo(() => getWorkoutTypeNames(), []);

  const educationCardsByType = useEducationSection();
  const runningEducationCards = useMemo(
    () => educationCardsByType[WahooWorkoutTypeFamily.Running] ?? [],
    [educationCardsByType]
  );

  const cyclingEducationCards = useMemo(
    () => educationCardsByType[WahooWorkoutTypeFamily.Cycling] ?? [],
    [educationCardsByType]
  );

  return (
    <>
      <GlobalHeader title={t`Athlete Profile`} disableScrollDivider />
      <ErrorBoundary
        isLoading={userFitnessProfilesIsLoading || fitnessProfileSettingsIsLoading}
        error={userFitnessProfilesError || fitnessProfileSettingsError}
        onReload={handleReload}
      >
        <Box>
          <Box bgcolor="background.level1" width="100%" position="sticky" top={0} zIndex={1}>
            <Container maxWidth="sm" disableGutters>
              <Box
                display="flex"
                flexDirection="row"
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Tabs
                  value={profile}
                  onChange={handleTabChange}
                  aria-label={t`Fitness profile type`}
                  sx={{ borderBottom: 0 }}
                  slotProps={{
                    indicator: {
                      sx: selectedFitnessProfiles.length === 1 ? { display: "none" } : {},
                    },
                  }}
                >
                  {selectedFitnessProfiles.map((profile) => {
                    const Icon = getWorkoutTypeFamilyWfIcon(profile);
                    const isRunning = profile === WahooWorkoutTypeFamily.Running;
                    return (
                      <Tab
                        key={profile}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            {workoutTypeNames[WahooWorkoutTypeFamily[profile]]}
                            {isRunning && showBetaTag && (
                              <RedesignrChip label={t`Beta`} size="small" color="info" readOnly />
                            )}
                          </Box>
                        }
                        value={profile}
                        icon={<Icon />}
                        iconPosition="start"
                      />
                    );
                  })}
                </Tabs>
                <WfButton
                  hierarchy="secondary"
                  iconOnly
                  aria-label={t`Edit athlete profiles`}
                  onClick={handleEditProfileClick}
                  sx={{ mr: 2 }}
                >
                  <WfIconContentEditRegular fontSize="medium" />
                </WfButton>
              </Box>
            </Container>
            <Divider />
          </Box>
          {profile === WahooWorkoutTypeFamily.Cycling && (
            <CyclingProfile educationCards={cyclingEducationCards} />
          )}
          {profile === WahooWorkoutTypeFamily.Running && (
            <RunningProfile educationCards={runningEducationCards} />
          )}
        </Box>
        <FullScreenDialog
          slideDirection="up"
          backgroundColor="background.level1"
          open={profileSelectDialogOpen}
          handleClose={closeProfileSelectDialog}
          headerProps={{
            title: t`Edit`,
            primaryLeftActionItem: {
              action: closeProfileSelectDialog,
              icon: <WfIconNavigationCloseRegular />,
            },
            "data-testid": "select-profiles-dialog",
          }}
        >
          <ProfileSelectDialog
            selectedProfiles={selectedFitnessProfiles}
            onSave={handleProfileSelectionChange}
            onCancel={closeProfileSelectDialog}
          />
        </FullScreenDialog>
      </ErrorBoundary>
    </>
  );
};

export default FitnessProfile;
